/**
 * @file Analytics Service for PostHog tracking
 * @description Manages PostHog analytics integration for game events
 */
import posthog from 'posthog-js';
import { v4 as uuidv4 } from 'uuid';
import type { BaseEventProperties, TrackerEvents } from '../types/analytics';

declare const __GIT_HASH__: string;

export const POSTHOG_ENABLED_URLS = [
  'game-jam-hidden-obj-original.vercel.app',
  'clue-hunter-client.vercel.app',
  'clue-hunter-static.vercel.app',
  'clue-hunter.qa.wolf.games',
  'clue-hunter.staging.wolf.games',
  'clue-hunter.wolf.games',
];

// PostHog configuration
export const POSTHOG_API_KEY =
  'phc_mMnQU7obEkAR67pnZSB4QRu9qvdROORWJ5TqlSd5yes';
export const POSTHOG_API_HOST = 'https://us.i.posthog.com';
const PLATFORM = 'clue_hunter' as const;

// Game constants
const GAME_NAME = 'Clue Hunter';
const GAME_VERSION = __GIT_HASH__; // Git commit hash injected at build time

export class AnalyticsService {
  private static instance: AnalyticsService;
  private enabled = false;
  private userId: string = '';
  private userEmail: string = '';
  private userName: string = '';
  private caseName: string = '';
  private caseId: string = '';
  private testVariant?: 'A' | 'B' | 'C';
  private levelStartTimes: Map<string, number> = new Map();
  private screenStartTimes: Map<string, number> = new Map();
  private oneTimeEvents: Set<string> = new Set();
  private sessionId: string = uuidv4();
  private sessionStartTime: number = 0;

  private debugMode = false;

  private constructor() {}

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  init(caseName: string, caseId?: string, testVariant?: 'A' | 'B' | 'C') {
    this.caseName = caseName;
    this.caseId = caseId || '';
    this.testVariant = testVariant;
    this.enabled = this.shouldEnable();

    if (!this.enabled) {
      console.log('Analytics disabled for this environment');
      return;
    }

    if (!posthog.__loaded) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_API_HOST,
        person_profiles: 'always',
        capture_pageview: false,
        capture_pageleave: false,
        capture_exceptions: true,
        session_recording: {
          recordCrossOriginIframes: false,
        },
      });
    }

    this.identifyUser();
  }

  private shouldEnable(): boolean {
    if (this.debugMode) return true;
    return POSTHOG_ENABLED_URLS.some((url) =>
      window.location.host.includes(url),
    );
  }

  private getUserData() {
    const _params = new URLSearchParams(window.location.search);

    const uid =
      this.userId || localStorage.getItem('uid') || `anonymous_${uuidv4()}`;
    const email = this.userEmail || localStorage.getItem('email') || '';
    const name = this.userName || localStorage.getItem('name') || '';

    if (uid) localStorage.setItem('uid', uid);
    if (email) localStorage.setItem('email', email);
    if (name) localStorage.setItem('name', name);

    this.userId = uid;
    this.userEmail = email;
    this.userName = name;

    return { uid, email, name };
  }

  private identifyUser() {
    if (!this.enabled) return;

    const userData = this.getUserData();
    if (userData.uid) {
      posthog.identify(userData.uid, {
        email: userData.email,
        name: userData.name,
      });
    }
  }

  private getBaseProperties(): BaseEventProperties {
    // Calculate session duration in ms
    const sessionDuration =
      this.sessionStartTime > 0 ? Date.now() - this.sessionStartTime : 0;

    return {
      case_name: this.caseName,
      test_variant: this.testVariant,
      event_id: uuidv4(),
      timestamp: new Date().toISOString(),
      user_id: this.userId,
      user_email: this.userEmail || undefined,
      platform: PLATFORM,
      session_id: this.sessionId,
      game_name: GAME_NAME,
      game_version: GAME_VERSION,
      session_duration: sessionDuration,
    };
  }

  private getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/android|webos|iphone|ipod|blackberry|windows phone/i.test(userAgent)) {
      return 'mobile';
    }

    if (
      /ipad|tablet|playbook|silk/i.test(userAgent) ||
      (navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 2 &&
        /macintosh/i.test(userAgent))
    ) {
      return 'tablet';
    }

    return 'desktop';
  }

  track<T extends keyof TrackerEvents>(
    eventName: T,
    properties: Omit<TrackerEvents[T], keyof BaseEventProperties>,
  ) {
    if (!this.enabled && !this.debugMode) return;

    const eventData = {
      ...this.getBaseProperties(),
      ...properties,
    };

    if (this.enabled) {
      posthog.capture(eventName, eventData);
    }
  }

  trackOnce<T extends keyof TrackerEvents>(
    eventName: T,
    properties: Omit<TrackerEvents[T], keyof BaseEventProperties>,
  ) {
    if (this.oneTimeEvents.has(eventName)) {
      return;
    }

    this.track(eventName, properties);
    this.oneTimeEvents.add(eventName);
  }

  reset() {
    this.oneTimeEvents.clear();
    this.levelStartTimes.clear();
    this.screenStartTimes.clear();
  }

  setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
    this.enabled = this.shouldEnable();
  }

  trackSessionStart(initialCaseName?: string) {
    if (this.sessionStartTime === 0) {
      this.sessionStartTime = Date.now();
    }

    this.track('session_start', {
      start_trigger: initialCaseName ? 'case' : 'game_menu',
      case_name_initial: initialCaseName,
      device_type: this.getDeviceType(),
    });
  }

  trackSessionEnd(endState: 'success' | 'quit' | 'error' | 'tab_close') {
    this.track('session_end', {
      session_end_state: endState,
    });
  }

  trackCaseStart() {
    this.track('case_start', {
      case_id: this.caseId,
    });
  }

  trackCaseEnd(
    caseCompleted: boolean,
    caseScore: string,
    retryCount: number,
    hintUsedCount: number,
    scoreBreakdown?: {
      base_evidence_points: number;
      time_efficiency_bonus: number;
      speed_streak_multiplier: number;
      completeness_bonus: number;
      accuracy_multiplier: number;
      total_score_earned: number;
    },
    failReason?: 'gameplay' | 'exit' | 'error',
    starReward?: number,
    badgeRewardTier?: number,
  ) {
    this.track('case_end', {
      case_id: this.caseId,
      case_completed: caseCompleted ? 'yes' : 'no',
      case_fail_reason: caseCompleted ? undefined : failReason,
      case_score: caseScore,
      case_retry_count: retryCount,
      hint_used_count: hintUsedCount,
      ...(scoreBreakdown || {
        base_evidence_points: 0,
        time_efficiency_bonus: 0,
        speed_streak_multiplier: 0,
        completeness_bonus: 0,
        accuracy_multiplier: 0,
        total_score_earned: 0,
      }),
      star_reward: starReward ?? 0,
      badge_reward_tier: badgeRewardTier ?? 0,
    });
  }

  trackScoreEarned(
    score: string,
    scoreBreakdown?: {
      base_evidence_points: number;
      time_efficiency_bonus: number;
      speed_streak_multiplier: number;
      completeness_bonus: number;
      accuracy_multiplier: number;
      total_score_earned: number;
    },
  ) {
    this.track('score_earned', {
      game_score: score,
      ...(scoreBreakdown || {
        base_evidence_points: 0,
        time_efficiency_bonus: 0,
        speed_streak_multiplier: 0,
        completeness_bonus: 0,
        accuracy_multiplier: 0,
        total_score_earned: 0,
      }),
    });
  }

  trackButtonClick(
    buttonLabel: string,
    elementId?: string,
    screenId?: string,
    inputMethod?: 'touch' | 'mouse' | 'controller',
  ) {
    this.track('button_click', {
      button_label: buttonLabel,
      element_id: elementId,
      screen_id: screenId,
      input_method: inputMethod,
    });
  }

  trackLevelStart(levelId: string, levelOrder: number) {
    this.levelStartTimes.set(levelId, Date.now());
    this.track('level_start', {
      level_id: levelId,
      case_id: this.caseId,
      level_order: levelOrder,
    });
  }

  trackLevelEnd(
    levelId: string,
    levelOrder: number,
    completed: 'success' | 'fail',
    score: string,
    cluesCollected: number,
    maxCluesCollected: boolean,
    videosSkipped: boolean,
    hasVideo: boolean,
    hintUsedCount: number,
    scoreBreakdown?: {
      base_evidence_points: number;
      time_efficiency_bonus: number;
      speed_streak_multiplier: number;
      completeness_bonus: number;
      accuracy_multiplier: number;
      total_score_earned: number;
    },
    keyItemsData?: {
      key_items_found: number;
      key_items_array: [string, string][];
    },
  ) {
    this.track('level_end', {
      level_id: levelId,
      case_id: this.caseId,
      level_order: levelOrder,
      level_completed: completed,
      level_score: score,
      clues_collected: cluesCollected,
      clues_max_number_collected: maxCluesCollected,
      videos_skipped: videosSkipped,
      has_video: hasVideo,
      hint_used_count: hintUsedCount,
      ...(scoreBreakdown || {
        base_evidence_points: 0,
        time_efficiency_bonus: 0,
        speed_streak_multiplier: 0,
        completeness_bonus: 0,
        accuracy_multiplier: 0,
        total_score_earned: 0,
      }),
      ...(keyItemsData || {
        key_items_found: 0,
        key_items_array: [],
      }),
    });
    this.levelStartTimes.delete(levelId);
  }

  trackLevelRetry(levelId: string, levelOrder: number) {
    this.track('level_retry', {
      level_id: levelId,
      case_id: this.caseId,
      level_order: levelOrder,
    });
  }

  trackCaseReplayed(retryCount: number) {
    this.track('case_replayed', {
      case_retry_count: retryCount,
      case_id: this.caseId,
    });
  }

  trackRoomUnlocked(roomId: string, levelId: string, levelOrder: number) {
    this.track('room_unlocked', {
      room_id: roomId,
      level_id: levelId,
      case_id: this.caseId,
      level_order: levelOrder,
    });
  }

  trackBonusCollected(bonusName: string, bonusValue: string) {
    this.track('bonus_collected', {
      bonus_name: bonusName,
      bonus_value: bonusValue,
    });
  }

  trackCluesCollected(
    cluesNumber: number,
    clueName: string,
    clueId: string,
    timeFromPreviousClue: number,
    hintUsed: boolean,
  ) {
    this.track('clues_collected', {
      clues_number: cluesNumber,
      clue_name: clueName,
      clue_id: clueId,
      time_from_previous_clue: timeFromPreviousClue,
      hint_used: hintUsed,
    });
  }

  trackSoundMute(muted: boolean) {
    this.track('sound_mute', {
      muted,
    });
  }

  trackScreenEnter(screenId: string, screenName: string, fromScreen?: string) {
    this.screenStartTimes.set(screenId, Date.now());
    this.track('screen_enter', {
      screen_id: screenId,
      screen_name: screenName,
      from_screen: fromScreen,
    });
  }

  trackScreenExit(screenId: string, screenName: string) {
    const startTime = this.screenStartTimes.get(screenId);
    const timeSpent = startTime ? (Date.now() - startTime) / 1000 : 0;

    this.track('screen_exit', {
      screen_id: screenId,
      screen_name: screenName,
      time_spent_seconds: timeSpent,
    });
    this.screenStartTimes.delete(screenId);
  }

  trackVideoSkipped(
    videoId: string,
    videoType:
      | 'cutscene'
      | 'interview'
      | 'transition'
      | 'tutorial'
      | 'narrative'
      | 'ftue',
    watchedSeconds: number,
  ) {
    this.track('video_skipped', {
      video_id: videoId,
      video_type: videoType,
      watched_seconds: watchedSeconds,
    });
  }

  trackFTUEVideoViewed() {
    this.track('ftue_video_viewed', {});
  }

  trackFTUEVideoSkipped(watchedSeconds: number) {
    this.track('ftue_video_skipped', {
      watched_seconds: watchedSeconds,
    });
  }

  trackIdleTimeout(screenId: string, idleSeconds: number) {
    this.track('idle_timeout', {
      screen_id: screenId,
      idle_seconds: idleSeconds,
    });
  }

  trackHintRequested(
    screenId: string,
    levelId: string,
    hintId: string,
    hintCount: number,
    levelOrder: number,
    hintCost?: { type: string; amount: number },
    remainingCurrency?: number,
  ) {
    this.track('hint_requested', {
      screen_id: screenId,
      level_id: levelId,
      hint_id: hintId,
      hint_count: hintCount,
      hint_cost: hintCost,
      remaining_currency: remainingCurrency,
      level_order: levelOrder,
    });
  }

  getFeatureFlag(flagName: string): string | boolean {
    if (!this.enabled) return false;
    const flagValue = posthog.getFeatureFlag(flagName);

    return flagValue;
  }

  // Error Capture Methods

  trackException(
    error: Error,
    errorContext?: string,
    componentName?: string,
    screenId?: string,
    userAction?: string,
  ) {
    if (this.enabled) {
      posthog.captureException(error, {
        context: errorContext,
        component: componentName,
        screen: screenId,
        user_action: userAction,
      });
    }
  }

  trackNetworkError(
    requestUrl: string,
    errorMessage: string,
    options?: {
      requestMethod?: string;
      errorCode?: string;
      responseStatus?: number;
      retryCount?: number;
    },
  ) {
    if (this.enabled) {
      const error = new Error(`Network Error: ${errorMessage}`);
      error.name = 'NetworkError';
      posthog.captureException(error, {
        request_url: requestUrl,
        request_method: options?.requestMethod,
        error_code: options?.errorCode,
        response_status: options?.responseStatus,
        retry_count: options?.retryCount,
      });
    }
  }

  trackAssetLoadError(
    assetUrl: string,
    assetType: 'image' | 'video' | 'audio' | 'script',
    errorMessage: string,
    options?: {
      loadAttemptDuration?: number;
      screenId?: string;
      context?: string;
    },
  ) {
    if (this.enabled) {
      const error = new Error(`Asset Load Error: ${errorMessage}`);
      error.name = 'AssetLoadError';
      posthog.captureException(error, {
        asset_url: assetUrl,
        asset_type: assetType,
        load_attempt_duration: options?.loadAttemptDuration,
        screen_id: options?.screenId,
        context: options?.context,
      });
    }
  }

  // Performance Tracking Methods

  trackAssetLoadTime(
    assetUrl: string,
    assetType: 'image' | 'video' | 'audio',
    loadDurationMs: number,
    options?: {
      assetSizeBytes?: number;
      screenId?: string;
      context?: string;
    },
  ) {
    this.track('asset_load_time', {
      asset_url: assetUrl,
      asset_type: assetType,
      load_duration_ms: loadDurationMs,
      asset_size_bytes: options?.assetSizeBytes,
      screen_id: options?.screenId,
      context: options?.context,
    });
  }

  trackSceneTransition(
    toSceneId: string,
    transitionDurationMs: number,
    locationId: string,
    levelOrder: number,
    fromSceneId?: string,
  ) {
    this.track('scene_transition', {
      from_scene_id: fromSceneId,
      to_scene_id: toSceneId,
      transition_duration_ms: transitionDurationMs,
      location_id: locationId,
      level_order: levelOrder,
    });
  }

  trackHotspotResponse(
    responseLatencyMs: number,
    isCorrectClick: boolean,
    levelId: string,
    itemId?: string,
  ) {
    this.track('hotspot_response', {
      response_latency_ms: responseLatencyMs,
      is_correct_click: isCorrectClick,
      item_id: itemId,
      screen_id: 'gameplay',
      level_id: levelId,
    });
  }

  // Dine Popup Tracking Methods

  trackDinePopupShown(params: {
    trigger: 'key_item' | 'location_cleared';
    itemId?: string;
    messageIndex: number;
  }) {
    this.track('dine_popup_shown', {
      trigger: params.trigger,
      item_id: params.itemId,
      message_index: params.messageIndex,
    });
  }

  trackDinePopupAdvanced(params: {
    messageIndex: number;
    timeOnBubbleMs: number;
  }) {
    this.track('dine_popup_advanced', {
      message_index: params.messageIndex,
      time_on_bubble_ms: params.timeOnBubbleMs,
    });
  }

  trackDinePopupDismissed(params: {
    trigger: 'key_item' | 'location_cleared';
    totalBubbles: number;
  }) {
    this.track('dine_popup_dismissed', {
      trigger: params.trigger,
      total_bubbles: params.totalBubbles,
    });
  }
}

// Export singleton instance
export const analytics = AnalyticsService.getInstance();
